import { Injectable } from '@angular/core';
import { AppErrorModel } from "../models/appError.model";

@Injectable({
  providedIn: 'root'
})
export class ErrorMapperService {

  /**
   * REGISTRATION
   * Rival error cases
   * 0 status - SYSTEM/network error
   * 401 auth 'invalid token' error - SESSION error
   * 404 not found on 'invalid base url' - SYSTEM error
   * 500 server error - SYSTEM error ex. server error 522 statusTest <none>
   * 200 error method not found on 'invalid method' - USER error because has error.error object with message
   * 200 error on 'invalid params' like field name error, empty details, etc --> FIELD error as error.data has missing/invalid fields
   * 200 error on 'values that the server rejected' --> FIELD error: email or username validation fails
   *
   * GamingPodium errors
   * 0 stystem network error => SYSTEM/network error
   * 401 unauthorized ex. invalid reg token => SESSION error
   * field validation errors: 400 bad request + error in error object => err.error.error.message tells us the problem = USER error + FIELD error on create
   * invalid url (err name not resolved) => system/network error
   * invalid method: 404 Not found => user error 'Not found' message - todo: could have a better error message ex. system
   * ? 500 server error - ?todo find out how this works
   * 429 error too many requests: error message is 'Try later' => USER error
   *
   *
   * API METHODS error cases ex. keepAlive, promotion.current
   * Rival
   * 0 status - SYSTEM/network error
   * 'invalid token' error: 200 OK error in response saying 'Could not authorize request.'- SESSION error
   * no token: 401 Unauthorized error in response saying 'Could not authenticate request.'- SESSION error
   * 404 not found on 'invalid base url' - SYSTEM error
   * 500 server error - SYSTEM error ex. server error 522 statusTest <none>
   * 200 error method not found on 'invalid method' on 1 or both - USER error with message
   * no params, values required: NO error on 'invalid params' + NO field errors on 'values that the server rejected'
   *
   * GamingPodium errors
   * 0 stystem network error => SYSTEM/network error
   * 401 unauthorized ex. invalid reg token => SESSION error
   * invalid url (err name not resolved, status 0) => system/network error
   * invalid method: 404 Not found => user error 'Not found' message
   * ? 500 server error - ?todo find out how this works
   * 429 error too many requests: error message is 'Try later' => USER error
   * no 200 OK errors, no field validation errors
   *
   * API METHODS without AUTH error cases ex. login, resetPassword + fetchGamesFrom CDN, fetchApiGames (work without token)
   * 0 network error => SYSTEM/network error
   * invalid credentials: rival 200ok + message, GP 401 + message => USER error
   * invalid response format: fallback error => SYSTEM error
   * other errors => make all system errors by checking status - todo later
   *   - 500 server error => SYSTEM/network error ?todo find out if it has message
   *   - invalid base url: 404 rival with ""error.message, 0 GP => SYSTEM/network error
   *   - invalid method 200ok Rival (error.message), 404 GP (error.message) => USER error (could be system error)
   *   - missing params: 200ok rival (error.message + data), 400 GP (error.message) => USER error (could be system error)
   *   - NO 429 too many requests error ?
   *
   * API METHODS with AUTH error cases ex. getUserInfo
   * 0 network error => SYSTEM/network error
   * invalid token => 401 rival + message "authenticate", 401 GP
   * no token: 200 ok rival + message "authorize" , 401 GP message Unauthorized
   * invalid response format: fallback error => SYSTEM
   * other errors => make all system errors by checking status - todo later
   *   - 500 server error => SYSTEM/network error ?todo find out if it has message
   *   - invalid base url: 404 rival with ""error.message, 0 GP => SYSTEM/network error
   *   - invalid method 200ok Rival (error.message), 404 GP (error.message) => USER error (could be system error)
   *   - having unnecessary params incorrect:200 ok rival (error.message) => USER error (could be system error) + 500GP
   *   - having unnecessary params correct format: NO error
   *   - NO 429 too many requests error ?
   * 
   * changePassword errors: mapAPIerrors
   * 0 network error => SYSTEM/network error
   * invalid token => 401 rival + message "authenticate", 401 GP
   * no token: 200 ok rival + message "authorize" , 401 GP message Unauthorized
   * invalid response format: fallback error => SYSTEM
   * other errors
   *   - 500 server error => SYSTEM/network error
   *   - invalid base url: 404 rival with "" error.message, 0 GP => SYSTEM/network error
   *   - invalid method 200 ok Rival (error.message), 404 GP (error.message) => USER error
   *   - having incorrect params: 500 rival, 400 GP (error.message) => USER error
   *   - invalid params:
   *     - oldPassword: 200 ok rival (error.message), 401 GP => USER/SESSION error
   *     - newPassword: 200 ok rival (error.message), 400 GP (error.message) => FIELD error
   *   - NO 429 too many requests error ?
   *
   */

  constructor() { }

  mapRegistrationError(err: any): AppErrorModel {
    // invalid regToken / unauthorized
    if (err.status === 401 || err?.error?.message === 'Could not authorize request.') {
      // console.log('invalid regToken - from error mapper');
      return {
        type: 'SESSION',
        // message: 'REGISTER.TOASTR_TOKEN_ERROR_MESSAGE',
        // title: 'REGISTER.TOASTR_TOKEN_ERROR_H1',
        raw: err
      }
    }

    // field validation errors (Rival-style JSON-RPC)
    if (err?.error?.data && Object.keys(err.error.data).length > 0) {
      // console.log(' field validation errors - from error mapper');

      const fieldErrors: Record<string, string> = {};
      for (const key of Object.keys(err.error.data)) {
        //addDetails throws errors like details.password
        const propName = key.split('.')[1] ?? key;
        //if 'details' is changed/missing it sends: {fieldErrors: {details: "This value should not be null."}} - todo: handle this case correctly
        //ex. not to add to fieldErrors / not to show to user / return as system error
        fieldErrors[propName] = err.error.data[key].message;
      }
      return {
        type: 'FIELD',
        message: err.error.message ?? 'Validation failed',
        fieldErrors,
        raw: err
      };
    }

    //GP errors: field validation, 429, invalid url, invalid method
    if (err?.error && err.error.error && err.error.jsonrpc) {
      // console.log('GP errors - from error mapper');

      //get fieldErrors from the message on CREATE:
      const regFields = ['email', 'login'] //todo: add other fields+ rewrite tests
      const fieldErrors: Record<string, string> = {};
      for (const fieldName of regFields) {
        if (err.error.error.message && err.error.error.message.toLowerCase().includes(fieldName))
          if (fieldName === 'login') {
            fieldErrors['username'] = err.error.error.message
          } else {
            fieldErrors[fieldName] = err.error.error.message;
          }
      }
      if (Object.keys(fieldErrors).length) {
        return {
          type: 'FIELD',
          message: err.error.message ?? 'Validation failed',
          fieldErrors,
          raw: err
        };
      }


      return {
        type: 'USER',
        message: err.error.error.message ?? 'ERRORS.GENERAL_TITLE',
        title: 'Registration error',
        raw: err
      };
    }

    // user-readable message: handled by API ex method not available, rate limit exceeded
    if (err?.error?.message) {
      // console.log('error message from server - from error mapper');
      return {
        type: 'USER',
        message: err.error.message,
        title: 'ERRORS.GENERAL_TITLE',
        raw: err
      };
    }

    //0 status code = network error
    if (err.status === 0) {
      return {
        type: 'SYSTEM',
        message: 'ERRORS.NETWORK_MESSAGE',
        title: 'ERRORS.NETWORK_TITLE',
        raw: err
      }
    }

    // fallback for unexpected / malformed errors ex 404 bad url, 500 server errors
    // console.log('fallback error - from error mapper');
    return {
      type: 'SYSTEM',
      message: 'ERRORS.GENERAL_MESSAGE',
      // title: 'Registration error + statuscode name(HttpErrorResponse)', //goal: cant have API url in toastr
      raw: err
    };

  }

  mapKeepAliveError(errorResponse: any): AppErrorModel {
    // invalid regToken / unauthorized
    // if (errorResponse.status === 401 || errorResponse?.error?.message === 'Could not authenticate request.'){ ?? Rival
    if (errorResponse.status === 401 || errorResponse?.error?.message === 'Could not authorize request.') {
      // console.log('invalid sessionToken - from error mapper');
      return {
        type: 'SESSION',
        // message: 'LOGIN.TOASTR_ERROR_LOGGEDOUT_MESSAGE',
        // title: 'LOGIN.TOASTR_ERROR_LOGGEDOUT_H1',
        raw: errorResponse
      }
    }

    //200 Ok status but error in response
    if (Array.isArray(errorResponse)) {
      // console.log('Error in response - from error mapper');
      for (const error of errorResponse) {
        if ('error' in error) {
          //missing token error
          if (error.error?.message === 'Could not authorize request.') {
            return {
              type: 'SESSION',
              raw: errorResponse
            }
          }

          //not fatal errors todo refactor this later: see in auth Service
          //now there is an error in 1 or both methods -> throw the first error
          return {
            type: 'USER',
            message: error.error?.message, //+'ACCOUNT.BALANCE_FAILED_TIP'
            // title: 'ACCOUNT.BALANCE_FAILED_MESSAGE',
            raw: errorResponse
          };
        }
      }
    }

    //0 status code = network error
    if (errorResponse.status === 0) {
      // console.log('network error - from error mapper');
      return {
        type: 'SYSTEM',
        message: 'Network error.', //+'ACCOUNT.BALANCE_FAILED_TIP'
        // title: 'ACCOUNT.BALANCE_FAILED_MESSAGE',
        raw: errorResponse
      }
    }

    // fallback for unexpected / malformed errors ex 404 bad url, 500 server errors
    // console.log('fallback error - from error mapper');
    return {
      type: 'SYSTEM',
      message: errorResponse.error?.message, //+'ACCOUNT.BALANCE_FAILED_TIP'
      // title: 'ACCOUNT.BALANCE_FAILED_MESSAGE',
      raw: errorResponse
    };

  }


  mapAPIerrors(errorResponse: any): AppErrorModel {
    // invalid token / unauthorized ex getUserInfo - not in login/resetPassword
    if (errorResponse.status === 401 || errorResponse?.error?.message === 'Could not authorize request.') {
      // console.log('invalid sessionToken - from error mapper');
      return {
        type: 'SESSION',
        raw: errorResponse
      }
    }

    //0 status code = network error
    if (errorResponse.status === 0) {
      // console.log('network error - from error mapper');
      return {
        type: 'SYSTEM',
        message: 'ERRORS.NETWORK_MESSAGE',
        title: 'ERRORS.NETWORK_TITLE',
        raw: errorResponse
      }
    }

    //error in response (200 OK status invalid credentials, missing params, invalid method)
    if ('error' in errorResponse && (errorResponse.error.message || errorResponse.status === 400)) {
      if (errorResponse.status === 400 || errorResponse.error.data) {
        //resetPassword: error.message.data.email from Rival, 404 bad request with error.error.message from GP
        // console.log('field validation errors - from error mapper');
        const fieldErrors: Record<string, string> = {};
        if (errorResponse.error.data && Object.keys(errorResponse.error.data).length > 0) {
          // field validation errors (Rival-style JSON-RPC)
          for (const key of Object.keys(errorResponse.error.data)) {
            const propName = key.split('.')[1] ?? key;
            fieldErrors[propName] = errorResponse.error.data[key].message;
          }
        }
        return {
          type: 'FIELD',
          message: errorResponse.error.error?.message || errorResponse.error.message, //GP error does not have data, but has message
          fieldErrors, //no field errors on GP
          raw: errorResponse
        };
      }
      // console.log('Error in response - from error mapper');
      return {
        type: 'USER',
        message: errorResponse.error.message,
        raw: errorResponse
      };
    }

    //unexpected / malformed errors + invalid base url, server error?
    // console.log('fallback error - from error mapper');
    return {
      type: 'SYSTEM',
      raw: errorResponse
    };

  }




  //helpers:
  //normalize GP response in batched calls (keepAlive + currentPromo)
  normalizeBatchResponse(responses: any[]) {
    return responses.map(item => {
      if (typeof item === 'string') {
        try {
          return JSON.parse(item.trim());
        } catch {
          return item;
        }
      }
      return item;
    });
  }
}
